import {
  buildClientNextStepRecommendations,
  buildClientWorkspace,
  getClientInactivitySignals,
  normalizeClientProfile,
  scoreClientRetentionRisk,
} from './clientCRM';
import { buildStudentProgressSummary } from './progressTracking';
import { selectParticipantSafeProgressionInputs, summarizeDeliveredParticipants } from './clientCoachingMemory';
import { isDeliveryBearing } from './lessonHandoff';

// ─────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────

function toDateValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toTimestampValue(value) {
  return toDateValue(value)?.getTime?.() || 0;
}

function assertClientSignalsParams(clientUserId, orgId) {
  if (!clientUserId) throw new Error('clientSignals: clientUserId is required');
  if (!orgId) throw new Error('clientSignals: orgId is required');
}

function resolveDataCompleteness(workspace) {
  const hasReview = (workspace.videos || []).some((v) => v.status === 'reviewed');
  // Pass 79B REPAIR §5 — "has delivered lesson evidence" is the canonical delivery boundary, not `status:'completed'`.
  const hasLesson = (workspace.lessons || []).some((l) => isDeliveryBearing(l));
  const hasBooking = (workspace.bookings || []).length > 0;

  if (hasReview && hasLesson && hasBooking) return 'full';
  if (hasReview || hasLesson || hasBooking) return 'partial';
  return 'minimal';
}

function extractReviewSignals(videos) {
  const reviewed = (videos || [])
    .filter((v) => v.status === 'reviewed')
    .sort((a, b) => toTimestampValue(b.reviewedAt || b.createdAt) - toTimestampValue(a.reviewedAt || a.createdAt));

  // Only scan the most recent 10 to keep computation bounded.
  const scanned = reviewed.slice(0, 10);

  const issueCounts = new Map();
  const strengthCounts = new Map();
  const variantSet = new Set();
  const drillsSeen = new Map();
  const recentDrills = [];

  scanned.forEach((video, index) => {
    // Pass 76 — canonical precedence: `signals` first, `derivedSignals` is the compat alias (both hold the
    // same value; see reviewAnalysis.getAnalysisSignals). Aligned here so every reader uses one precedence.
    const signals = video.analysis?.signals || video.analysis?.derivedSignals || {};

    Object.entries(signals).forEach(([key, value]) => {
      if (!key) return;
      if (value === true || value === 'issue' || value === 'focus') {
        issueCounts.set(key, (issueCounts.get(key) || 0) + 1);
      } else if (value === 'strength' || value === 'positive') {
        strengthCounts.set(key, (strengthCounts.get(key) || 0) + 1);
      }
    });

    if (video.analysis?.variant) variantSet.add(video.analysis.variant);

    // Collect drills from the 3 most recent reviews only.
    if (index < 3) {
      (video.reviewDrills || []).forEach((drill) => {
        const key = drill.drillId || drill.topic || '';
        if (!key || drillsSeen.has(key)) return;
        drillsSeen.set(key, true);
        recentDrills.push({
          drillId: drill.drillId || '',
          topic: drill.topic || '',
          focusAreas: Array.isArray(drill.focusAreas) ? drill.focusAreas : [],
        });
      });
    }
  });

  const recurringIssues = Array.from(issueCounts.entries())
    .filter(([, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1])
    .map(([key]) => key);

  const recurringStrengths = Array.from(strengthCounts.entries())
    .filter(([, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1])
    .map(([key]) => key);

  const lastReviewedAt = scanned.length > 0
    ? toDateValue(scanned[0].reviewedAt || scanned[0].createdAt)
    : null;

  return {
    reviewedCount: reviewed.length,
    lastReviewedAt,
    recurringIssues,
    recurringStrengths,
    recentDrills,
    analysisVariants: Array.from(variantSet),
  };
}

function extractPackageSignals(packageSummary, now = new Date()) {
  const memberships = packageSummary?.activeMemberships || [];
  const expiryWindowMs = 14 * 24 * 60 * 60 * 1000;
  const windowEdge = now.getTime() + expiryWindowMs;

  let remainingSessions = 0;
  let remainingCredits = 0;
  let packageExpiryRisk = false;
  let nearestExpiryAt = null;
  const packageNames = [];

  memberships.forEach((membership) => {
    remainingSessions += membership.remainingSessions || 0;
    remainingCredits += membership.remainingCredits || 0;

    const expiryDate = toDateValue(membership.endAt);
    if (expiryDate) {
      const expiryMs = expiryDate.getTime();
      if (expiryMs <= windowEdge && expiryMs >= now.getTime()) {
        packageExpiryRisk = true;
      }
      if (!nearestExpiryAt || expiryMs < toTimestampValue(nearestExpiryAt)) {
        nearestExpiryAt = expiryDate;
      }
    }

    if (membership.packageName || membership.name) {
      packageNames.push(membership.packageName || membership.name);
    }
  });

  return {
    activePackageCount: memberships.length,
    remainingSessions,
    remainingCredits,
    packageExpiryRisk,
    nearestExpiryAt,
    packageNames,
  };
}

function resolveUpcomingBooking(bookings = []) {
  return (
    bookings
      .filter((b) => ['confirmed', 'upcoming', 'in_progress'].includes(b.status || ''))
      .sort((a, b) => toTimestampValue(a.startAt) - toTimestampValue(b.startAt))[0] || null
  );
}

function resolveSport(clientProfile, videos = []) {
  if (clientProfile.primaryDiscipline) return clientProfile.primaryDiscipline;
  const firstVideo = (videos || []).find((v) => v.sport);
  return firstVideo?.sport || '';
}

// ─────────────────────────────────────────────────────────────
// Core assembly — synchronous after workspace is loaded
// ─────────────────────────────────────────────────────────────

// Pass 79 §39 — a COMMERCIAL client may be a FAMILY (multiple explicit participants under one clientId). A single
// progression summary over the whole workspace would fabricate "the client's progression" by collapsing Child A +
// Child B. Progression here is only meaningful when there is exactly ONE explicit participant; otherwise it is absent
// (participant-scoped coaching memory lives in workspace.coachingMemory). Grouping is by explicit studentId only.
const EMPTY_PROGRESSION_SUMMARY = {
  reviewedCount: 0, recentReviewCount: 0, dominantSport: '', currentLevel: '', levelBasis: 'none',
  hasProgressionEvidence: false, topFocusAreas: [], progressionInput: null, progressionRecommendation: null,
  recommendationReason: '', activeTrack: null, currentSkill: null, nextSkill: null, lessonFlow: null,
  lessonFlowStages: [], completedLessonCount: 0, lessonReinforcementText: '', lessonSignals: {},
};

function assembleSignals(workspace, orgId, { services = [], now = new Date() } = {}) {
  const clientProfile = normalizeClientProfile(workspace.clientProfile || {});

  // Pass 79 REPAIR §6/§7 — FAIL CLOSED. A workspace-level personal progression summary is formed ONLY when there is
  // exactly ONE explicit participant and NO unlinked delivered coaching event, and it is fed PARTICIPANT-FILTERED
  // lessons/videos (never the whole-workspace arrays). "One known participant" is not proof that unlinked lessons or
  // a purchaser's video belong to them. Otherwise progression is UNAVAILABLE (participant-scoped coaching memory is
  // the source for coaching context).
  // Pass 79B REPAIR §4 — participantCount / isMultiParticipant / progression availability all derive from the ONE
  // canonical delivered-participant projection (isDeliveryBearing), so they can never disagree.
  const { participantCount, hasUnlinked } = summarizeDeliveredParticipants(workspace);
  const safeInputs = selectParticipantSafeProgressionInputs(workspace);
  const isMultiParticipant = participantCount > 1;
  const progressionSummary = safeInputs
    ? buildStudentProgressSummary(safeInputs.videos, { feedbackMap: {}, lessonInstances: safeInputs.lessons })
    : EMPTY_PROGRESSION_SUMMARY;

  const inactivitySignals = getClientInactivitySignals(clientProfile, {
    bookings: workspace.bookings || [],
    lessons: workspace.lessons || [],
    feedbackHistory: workspace.feedbackHistory || [],
    memberships: workspace.packageSummary?.activeMemberships || [],
    now,
  });

  const retentionRisk = scoreClientRetentionRisk(inactivitySignals);

  const reviewSignals = extractReviewSignals(workspace.videos || []);

  const packageSignals = extractPackageSignals(workspace.packageSummary, now);

  const heuristicRecommendations = buildClientNextStepRecommendations(clientProfile, {
    bookings: workspace.bookings || [],
    services: services || [],
    memberships: workspace.packageSummary?.activeMemberships || [],
    servicePackages: workspace.packageSummary?.servicePackages || [],
    feedbackHistory: workspace.feedbackHistory || [],
    now,
  });

  const upcomingBooking = resolveUpcomingBooking(workspace.bookings || []);
  const dataCompleteness = resolveDataCompleteness(workspace);
  const sport = resolveSport(clientProfile, workspace.videos);

  return {
    // Identity & Scope
    clientUserId: clientProfile.userId,
    orgId,
    displayName: clientProfile.displayName,
    email: clientProfile.email,
    sport,
    skillLevel: clientProfile.skillLevel,

    // Progression state (participant-scoped; absent for a multi-participant family or ambiguous ownership)
    progressionSummary,
    isMultiParticipant,
    participantCount,
    hasUnlinkedDeliveredLesson: hasUnlinked,

    // Review signals
    reviewedCount: reviewSignals.reviewedCount,
    lastReviewedAt: reviewSignals.lastReviewedAt,
    recurringIssues: reviewSignals.recurringIssues,
    recurringStrengths: reviewSignals.recurringStrengths,
    recentDrills: reviewSignals.recentDrills,
    analysisVariants: reviewSignals.analysisVariants,

    // Booking / session state
    lastBookingAt: toDateValue(clientProfile.lastBookingAt),
    daysSinceLastBooking: inactivitySignals.daysSinceLastActivity,
    hasUpcomingBooking: inactivitySignals.hasUpcomingBooking,
    upcomingBooking,
    averageCadenceDays: inactivitySignals.averageCadenceDays || null,

    // Package / commercial state
    activePackageCount: packageSignals.activePackageCount,
    remainingSessions: packageSignals.remainingSessions,
    remainingCredits: packageSignals.remainingCredits,
    packageExpiryRisk: packageSignals.packageExpiryRisk,
    nearestExpiryAt: packageSignals.nearestExpiryAt,
    packageNames: packageSignals.packageNames,

    // Inactivity state
    lastActivityAt: toDateValue(inactivitySignals.lastActivityAt),
    daysSinceLastActivity: inactivitySignals.daysSinceLastActivity,
    lastLessonAt: toDateValue(inactivitySignals.lastLessonAt),
    lastFeedbackAt: toDateValue(inactivitySignals.lastFeedbackAt),
    lastMessageAt: toDateValue(inactivitySignals.lastMessageAt),

    // Retention risk
    retentionRisk,

    // CRM context — note text is deliberately excluded; only count is exposed
    goals: clientProfile.goals,
    tags: clientProfile.tags,
    internalNotesCount: (workspace.clientNotes || []).length,

    // Heuristic recommendations
    heuristicRecommendations,

    // Metadata
    generatedAt: now,
    dataCompleteness,
  };
}

// ─────────────────────────────────────────────────────────────
// Exported functions
// ─────────────────────────────────────────────────────────────

/**
 * Builds the full client signals object by loading the workspace from Firestore
 * and then running all signal assembly computations.
 *
 * Use this when the workspace has not already been loaded by the caller.
 * Use buildClientSignalsFromWorkspace() if you already hold the workspace.
 *
 * @param {object} db - Firestore db instance
 * @param {string} clientUserId - The client's user ID
 * @param {string} orgId - The organization ID (required — prevents cross-org leakage)
 * @param {object} [options]
 * @param {Array}  [options.services] - Org services for recommendation context
 * @param {Date}   [options.now] - Override current time (for testing)
 * @returns {Promise<object>} Structured client signals
 */
export async function buildClientSignals(db, clientUserId, orgId, { services = [], now = new Date() } = {}) {
  assertClientSignalsParams(clientUserId, orgId);

  const workspace = await buildClientWorkspace(db, {
    organizationId: orgId,
    clientUserId,
    persistProfile: false,
    services,
  });

  return assembleSignals(workspace, orgId, { services, now });
}

/**
 * Builds client signals from an already-loaded workspace.
 * Synchronous — no additional Firestore reads.
 *
 * Use this when the caller (e.g. ClientProfilePage, RetentionQueuePage)
 * has already called buildClientWorkspace. Avoids loading Firestore twice.
 *
 * @param {object} workspace - Result of buildClientWorkspace()
 * @param {string} orgId - The organization ID (required)
 * @param {object} [options]
 * @param {Array}  [options.services] - Org services for recommendation context
 * @param {Date}   [options.now] - Override current time (for testing)
 * @returns {object} Structured client signals
 */
export function buildClientSignalsFromWorkspace(workspace, orgId, { services = [], now = new Date() } = {}) {
  if (!orgId) throw new Error('clientSignals: orgId is required');
  return assembleSignals(workspace || {}, orgId, { services, now });
}

/**
 * Returns a signals object with safe empty defaults for every field.
 * Use when consuming signals from an external source (e.g. cache) where
 * field presence cannot be guaranteed.
 *
 * @param {object} [signals]
 * @returns {object}
 */
export function normalizeClientSignals(signals = {}) {
  const s = signals || {};
  return {
    clientUserId: s.clientUserId || '',
    orgId: s.orgId || '',
    displayName: s.displayName || '',
    email: s.email || '',
    sport: s.sport || '',
    skillLevel: s.skillLevel || '',

    progressionSummary: s.progressionSummary || {
      reviewedCount: 0,
      recentReviewCount: 0,
      dominantSport: '',
      currentLevel: '',
      topFocusAreas: [],
      currentSkill: null,
      nextSkill: null,
      activeTrack: null,
      lessonFlow: null,
      lessonFlowStages: [],
      recommendationReason: '',
      completedLessonCount: 0,
      lessonReinforcementText: '',
      lessonSignals: {},
    },

    reviewedCount: s.reviewedCount || 0,
    lastReviewedAt: s.lastReviewedAt || null,
    recurringIssues: Array.isArray(s.recurringIssues) ? s.recurringIssues : [],
    recurringStrengths: Array.isArray(s.recurringStrengths) ? s.recurringStrengths : [],
    recentDrills: Array.isArray(s.recentDrills) ? s.recentDrills : [],
    analysisVariants: Array.isArray(s.analysisVariants) ? s.analysisVariants : [],

    lastBookingAt: s.lastBookingAt || null,
    daysSinceLastBooking: s.daysSinceLastBooking || 0,
    hasUpcomingBooking: s.hasUpcomingBooking || false,
    upcomingBooking: s.upcomingBooking || null,
    averageCadenceDays: s.averageCadenceDays || null,

    activePackageCount: s.activePackageCount || 0,
    remainingSessions: s.remainingSessions || 0,
    remainingCredits: s.remainingCredits || 0,
    packageExpiryRisk: s.packageExpiryRisk || false,
    nearestExpiryAt: s.nearestExpiryAt || null,
    packageNames: Array.isArray(s.packageNames) ? s.packageNames : [],

    lastActivityAt: s.lastActivityAt || null,
    daysSinceLastActivity: s.daysSinceLastActivity || 0,
    lastLessonAt: s.lastLessonAt || null,
    lastFeedbackAt: s.lastFeedbackAt || null,
    lastMessageAt: s.lastMessageAt || null,

    retentionRisk: s.retentionRisk || { score: 0, level: 'low', reasons: [] },

    goals: s.goals || '',
    tags: Array.isArray(s.tags) ? s.tags : [],
    internalNotesCount: s.internalNotesCount || 0,

    heuristicRecommendations: s.heuristicRecommendations || {
      nextService: null,
      packageSuggestion: null,
      rebookTiming: null,
      coachingFocus: '',
      followUpMessage: '',
    },

    generatedAt: s.generatedAt || null,
    dataCompleteness: s.dataCompleteness || 'minimal',
  };
}
